/* línkè · 我的气泡颜色中枢（2026-07-09 单色；2026-07-12 升级 Telegram 渐变） ==========
   用户选 1~3 个颜色 → <html> 挂属性 + 内联变量，Chat.css 接管层染「你」的气泡。
   - 1 个色：纯色半透明气泡（= 2026-07-09 老效果，行为不变）。
   - 2~3 个色：Telegram 那套「屏幕背后铺一张竖直渐变布，每个气泡只剪出自己
     位置那一块」——单个气泡近乎纯色，一屏连起来是流动渐变，滚动时颜色会流
     （流动由 bubbleFlow.js 每帧驱动 background-position；这里只负责渐变布本身）。
   「跟随主题」= 空数组 = 摘掉属性，回各主题原色（深/浅=紫，绯霞=玫红）。
   存 localStorage、每台设备各记（和主题/强调色/背景色同待遇，不进云同步）。
   ================================================================ */
import { useState, useEffect } from 'react';

export const STORAGE_USER_BUBBLE = 'lingke.userBubble';
export const MAX_BUBBLE_COLORS = 3;

/* 预设色板：挑的都是半透明叠加后在三主题上都好看的中明度色。
   id=null 是「跟随主题」（SideDrawer 里特殊渲染）。 */
export const USER_BUBBLE_PRESETS = [
  { id: null, name: '跟随主题' },
  { id: '#9a86c8', name: '丁香紫' },
  { id: '#ff5b8d', name: '玫粉' },
  { id: '#ff9a76', name: '蜜桃' },
  { id: '#5cc2a8', name: '薄荷' },
  { id: '#6fa8e0', name: '雾蓝' },
  { id: '#d98cc0', name: '藕荷' },
];

const _listeners = new Set();

function _valid(v) {
  return typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v);
}

/** 把存储里的原始值规整成合法颜色数组（0~3 个）。纯函数，进单测。
    兼容三种来路：JSON 数组（新）、单个 '#hex'（2026-07-09 老值）、空/坏值。 */
export function normalizeColors(raw) {
  let arr = [];
  if (Array.isArray(raw)) arr = raw;
  else if (_valid(raw)) arr = [raw]; // 老单色值平滑升级
  else if (typeof raw === 'string' && raw.trim().startsWith('[')) {
    try { arr = JSON.parse(raw); } catch { arr = []; }
  }
  return (Array.isArray(arr) ? arr : []).filter(_valid).slice(0, MAX_BUBBLE_COLORS);
}

export function getUserBubbleColors() {
  try {
    return normalizeColors(localStorage.getItem(STORAGE_USER_BUBBLE));
  } catch {
    return [];
  }
}

/** '#rrggbb' + alpha → 'rgba(r,g,b,a)'。纯函数，进单测。 */
export function hexToRgba(hex, a) {
  if (!_valid(hex)) return null;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

/* 渐变布用半透明色叠在玻璃气泡上：既保留原本的通透质感、深色文字也压得住，
   滚动时色相变化仍看得见。alpha 挑 0.34，观感与单色版 color-mix(~22%) 相近。
   SOLID 版（实心气泡开关）用高 alpha，让气泡在壁纸上也清清楚楚。 */
export const GRAD_ALPHA = 0.34;
export const GRAD_ALPHA_SOLID = 0.92;

/** 从颜色数组拼一条竖直渐变（Telegram 从上到下）。1 色时两端同色=纯色。 */
export function bubbleGradient(colors, alpha = GRAD_ALPHA) {
  const c = normalizeColors(colors);
  if (!c.length) return null;
  const stops = (c.length === 1 ? [c[0], c[0]] : c).map((h) => hexToRgba(h, alpha));
  return `linear-gradient(180deg, ${stops.join(', ')})`;
}

export function applyUserBubble(colors) {
  const root = document.documentElement;
  const c = normalizeColors(colors);
  if (!c.length) {
    root.style.removeProperty('--user-bubble');
    root.style.removeProperty('--ub-grad');
    root.removeAttribute('data-user-bubble');
    root.removeAttribute('data-user-bubble-grad');
    return;
  }
  // 主色（描边、单色背景、渐变第一段都用它）——兼容 2026-07-09 老 CSS 变量
  root.style.setProperty('--user-bubble', c[0]);
  root.setAttribute('data-user-bubble', '');
  if (c.length >= 2) {
    root.style.setProperty('--ub-grad', bubbleGradient(c));
    root.style.setProperty('--ub-grad-solid', bubbleGradient(c, GRAD_ALPHA_SOLID));
    root.setAttribute('data-user-bubble-grad', '');
  } else {
    root.style.removeProperty('--ub-grad');
    root.style.removeProperty('--ub-grad-solid');
    root.removeAttribute('data-user-bubble-grad');
  }
}

/* —— 实心气泡开关（2026-07-12，铃点的）：壁纸上半透明气泡发虚 → 一键变清晰。
   独立 flag，默认关（保持玻璃质感）。开 = html[data-bubble-solid]。 —— */
export const STORAGE_BUBBLE_SOLID = 'lingke.bubbleSolid';
const _solidListeners = new Set();

export function getBubbleSolid() {
  try {
    return localStorage.getItem(STORAGE_BUBBLE_SOLID) === '1';
  } catch {
    return false;
  }
}

export function applyBubbleSolid(on) {
  const root = document.documentElement;
  if (on) root.setAttribute('data-bubble-solid', '');
  else root.removeAttribute('data-bubble-solid');
}

export function setBubbleSolid(on) {
  try {
    if (on) localStorage.setItem(STORAGE_BUBBLE_SOLID, '1');
    else localStorage.removeItem(STORAGE_BUBBLE_SOLID);
  } catch {}
  applyBubbleSolid(on);
  _solidListeners.forEach((fn) => {
    try { fn(on); } catch {}
  });
}

export function useBubbleSolid() {
  const [on, set] = useState(() => getBubbleSolid());
  useEffect(() => {
    _solidListeners.add(set);
    return () => _solidListeners.delete(set);
  }, []);
  return [on, setBubbleSolid];
}

export function setUserBubbleColors(colors) {
  const c = normalizeColors(colors);
  try {
    if (c.length) localStorage.setItem(STORAGE_USER_BUBBLE, JSON.stringify(c));
    else localStorage.removeItem(STORAGE_USER_BUBBLE);
  } catch {}
  applyUserBubble(c);
  _listeners.forEach((fn) => {
    try { fn(c); } catch {}
  });
}

/* —— 旧接口保留：setUserBubble(单 hex 或 null)（万一别处还在用）—— */
export function getUserBubble() {
  const c = getUserBubbleColors();
  return c[0] || null;
}
export function setUserBubble(hex) {
  setUserBubbleColors(_valid(hex) ? [hex] : []);
}

/* 启动时（main.jsx，首帧前）恢复上次选色 + 实心开关，避免刷新闪回原样。 */
export function applyStoredUserBubble() {
  applyUserBubble(getUserBubbleColors());
  applyBubbleSolid(getBubbleSolid());
}

/* React hook：[colors[], setUserBubbleColors]。空数组 = 跟随主题。 */
export function useUserBubble() {
  const [ub, set] = useState(() => getUserBubbleColors());
  useEffect(() => {
    _listeners.add(set);
    return () => _listeners.delete(set);
  }, []);
  return [ub, setUserBubbleColors];
}
